import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Group, Pagination, Stack, Text } from '@mantine/core';
import { ProtectedComponent } from '@components/ProtectedComponent';
import { FallbackType, UserRole } from '@shared-types/auth.types';
import { PageHeader } from '@components/ui/PageHeader';
import { licenseViewerQueryOptions } from '@api/admin/adminQueries';
import { AdminPendingComponent } from '@features/admin/components/AdminPendingComponent';
import { AdminErrorComponent } from '@features/admin/components/AdminErrorComponent';
import { LicenseViewerFilters } from '@features/admin/components/license-viewer/LicenseViewerFilters';
import { LicenseViewerTable } from '@features/admin/components/license-viewer/LicenseViewerTable';
import { useLicenseViewerSearchParams } from '@features/admin/components/license-viewer/useLicenseViewerSearchParams';
import { licenseViewerSearchSchema } from '@features/admin/domain/admin.schemas';

export const Route = createFileRoute('/_authed/admin/license-viewer/')({
  validateSearch: zodValidator(licenseViewerSearchSchema),
  loaderDeps: ({search}) => search,
  loader: ({context: {queryClient}, deps}) =>
    queryClient.ensureQueryData(licenseViewerQueryOptions(deps)),
  pendingComponent: AdminPendingComponent,
  errorComponent: AdminErrorComponent,
  component: LicenseViewerRoute,
});

function LicenseViewerRoute() {
  const {searchParams, updateSearchParams} = useLicenseViewerSearchParams();
  const {data, isFetching} = useSuspenseQuery(licenseViewerQueryOptions(searchParams));

  const totalPages = Math.max(1, Math.ceil(data.totalCount / data.pageSize));

  return (
    <ProtectedComponent
      requiredRole={UserRole.Admin}
      fallbackType={FallbackType.Page}
      fallbackTitle="License Viewer"
      fallbackAction="view licenses"
    >
      <Stack gap="lg">
        <PageHeader
          title="License Viewer"
          subtitle="Cross-system license visibility"
          backButton={false}
        />
        <LicenseViewerFilters/>
        <LicenseViewerTable licenses={data.licenses} isLoading={isFetching}/>
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            {data.totalCount > 0
              ? `Showing ${((data.page - 1) * data.pageSize) + 1}-${Math.min(data.page * data.pageSize, data.totalCount)} of ${data.totalCount} licenses`
              : 'No licenses to display'}
          </Text>
          {totalPages > 1 && (
            <Pagination
              value={data.page}
              onChange={(page) => updateSearchParams({page}, false)}
              total={totalPages}
              size="sm"
              aria-label="license-viewer-pagination"
            />
          )}
        </Group>
      </Stack>
    </ProtectedComponent>
  );
}
